<template>
  <Form ref="userChangeForm" @submit="submitPasswordChange">
    <base-password
      name="currentPassword"
      autocomplete="current-password"
      :minlength="minimumPasswordLength"
      :validation-rules="currentPasswordValidationRules"
      required
      :model-value="currentPassword"
      @update:model-value="updateCurrentPassword($event)"
    >
      <template #label>
        {{ $t('Current password') }}
      </template>
    </base-password>
    <base-password
      name="newPassword"
      autocomplete="new-password"
      :minlength="minimumPasswordLength"
      :validation-rules="newPasswordValidationRules"
      required
      :model-value="newPassword"
      @update:model-value="updateNewPassword($event)"
    >
      <template #helpText>
        <translate
          :tt="{ minimumPasswordLength, suggestedMinimumPasswordLength }"
        >
          Password is case sensitive: must be at least {minimumPasswordLength}
          letters ({suggestedMinimumPasswordLength} recommended)
        </translate>
      </template>
      <template #label>
        {{ $t('New password') }}
      </template>
    </base-password>
    <div class="action-buttons">
      <a class="button minimal cancel" @click.prevent="$emit('cancel')">{{
        $t('Cancel')
      }}</a>
      <base-submit-button
        :submitting="state && state.matches('changing')"
        class="primary asblock"
      >
        {{ $t('Save') }}
        <template #submittingText>
          <translate>Saving...</translate>
        </template>
      </base-submit-button>
    </div>

    <pu-modal
      class="reset-password"
      :use-portal="false"
      :modal-class="'reset-password-modal'"
      modal-hash="reset-password"
    >
      <template #modal-trigger>
        <a class="pw-link">{{ $t('Forgot password') }} </a>
      </template>
      <reset-password :user-email="userEmail" />
    </pu-modal>
  </Form>
</template>

<script lang="ts">
import { Form } from 'vee-validate'
import {
  Component,
  Setup,
  Vue,
  Watch,
  toNative,
  Prop,
} from '~/utility/pu-class-decorator'
import {
  ChangeUserPasswordContext,
  ChangeUserPasswordMachineInterpreter,
  returnChangeUserPasswordMachine,
} from '~/apps/change-password/machine'
import { ChangePasswordErrors } from '~/apps/change-password/types'
import * as z from 'zod'
import { interpret } from 'xstate-v4'

@Component({
  components: { Form },
  inheritAttrs: false,
})
class UserChangePassword extends Vue {
  declare $refs!: {
    userChangeForm: InstanceType<typeof Form>
  }

  @Prop()
  userEmail?: string

  minimumPasswordLength = 6
  suggestedMinimumPasswordLength = 8

  context: ChangeUserPasswordContext | null = null
  state: ChangeUserPasswordMachineInterpreter['state'] | null = null

  currentPasswordValidationRules = z
    .string({ required_error: 'required' })
    .min(this.minimumPasswordLength, { message: 'min' })

  newPasswordValidationRules = z
    .string({ required_error: 'required' })
    .min(this.minimumPasswordLength, { message: 'min' })

  stateStrings: string[] = []

  @Setup(() => {
    return interpret(returnChangeUserPasswordMachine())
  })
  changeUserPasswordService: ChangeUserPasswordMachineInterpreter

  mounted() {
    this.registerToChangeUserPasswordMachine()
  }

  get currentPassword() {
    return this.context?.currentPassword || ''
  }

  get errors(): ChangePasswordErrors {
    return this.context?.errors || {}
  }

  get newPassword() {
    return this.context?.newPassword || ''
  }

  @Watch('errors')
  onErrorsChanged(errors: ChangePasswordErrors) {
    this.$refs.userChangeForm.setErrors(errors as Record<string, string[]>)
  }

  submitPasswordChange() {
    this.changeUserPasswordService.send('SUBMIT')
    // reportError(new Error(JSON.stringify(errors)))
  }

  updateCurrentPassword(value: string) {
    this.changeUserPasswordService.send('CHANGE_CURRENT_PASSWORD', { value })
  }

  updateNewPassword(value: string) {
    this.changeUserPasswordService.send('CHANGE_NEW_PASSWORD', { value })
  }

  private registerToChangeUserPasswordMachine() {
    this.changeUserPasswordService
      .onTransition((state) => {
        if (state.changed) {
          this.state = state
          this.stateStrings = state.toStrings()
          this.context = state.context
        }
      })
      .onDone(() => {
        this.$emit('submit')
      })
      .start()
  }
}

export default toNative(UserChangePassword)
</script>

<style scoped>
.action-buttons {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  margin: 2em 0 0;
  padding-bottom: 0.5em;
  gap: 2em;
}

.cancel {
  margin-top: 0.25em;
}

form :deep(> label:last-of-type) {
  display: block;
  margin: 2em 0 0;
}

form :deep(.reset-password .modal-body) {
  padding: 1.4em;
}

.pw-link {
  font-size: var(--body);
  margin-top: 1em;
  display: block;
}
</style>
